import { todayPT } from "@homebound/activesupport";
import {
  BoundCheckboxField,
  BoundRadioGroupField,
  BoundSelectField,
  Button,
  Css,
  FormLines,
  ModalBody,
  ModalFooter,
  ModalHeader,
  useComputed,
  useModal,
  useTestIds,
} from "@homebound/beam";
import { ObjectConfig, useFormState } from "@homebound/form-state";
import { useCallback, useMemo } from "react";
import { Link } from "react-router-dom";
import { BidContractType, InternalEstimateType, Maybe, SaveBidPackageRequestInput } from "src/generated/graphqlTypes";
import {
  BidPackageDetailPageDocument,
  useSaveBidPackageRequestMutation,
} from "src/routes/bids/BidPackageDetailPage.generated";
import {
  AddTradePartnerModal_BidPackageFragment,
  BidPackageDetailPage_TradeFragment,
  useAddTradePartnerModalQuery,
} from "src/routes/bids/components/AddTradePartnerModal.generated";
import { useCreateBidContractMutation } from "src/routes/developments/procurement/components/CreateDevelopmentContractButton.generated";
import { createTradesTradePartnersUrl } from "src/RouteUrls";
import { queryResult } from "src/utils";
import { PlanBasedBidsTableV2_BidPlanChildGroupsDocument } from "../plan-based-bid-package/components/PlanBasedBidsTableV2.generated";
import { INTERNAL_ESTIMATE_ID } from "../plan-based-bid-package/utils";

export function AddTradePartnerModal(props: { bidPackage: AddTradePartnerModal_BidPackageFragment }) {
  const { bidPackage } = props;
  const query = useAddTradePartnerModalQuery({
    variables: {
      filter: {
        costCodes: bidPackage.costCodes.map((cc) => cc.id),
        markets: bidPackage.isPlanBasedV2 ? [bidPackage.relevantMarket.id] : undefined,
      },
    },
  });
  return queryResult(query, ({ tradePartners }) => (
    <AddTradePartnerModalView trades={tradePartners} bidPackage={bidPackage} />
  ));
}

function AddTradePartnerModalView(props: {
  trades: BidPackageDetailPage_TradeFragment[];
  bidPackage: AddTradePartnerModal_BidPackageFragment;
}) {
  const { trades, bidPackage } = props;
  const tid = useTestIds({});
  // Offer the internal estimate trade unless the package already has one
  const showInternalEstimate = !bidPackage.internalEstimateBidContract;
  const { closeModal } = useModal();
  const [saveBidPackageRequest] = useSaveBidPackageRequestMutation();
  const [createBidContract] = useCreateBidContractMutation({
    refetchQueries: [BidPackageDetailPageDocument, PlanBasedBidsTableV2_BidPlanChildGroupsDocument],
  });
  const matchingTrades = useMemo(
    () => [
      ...trades
        .filter((tp) => bidPackage.costCodes.every((bpcc) => tp.costCodes.map((cc) => cc.id).includes(bpcc.id)))
        .filter((tp) => !bidPackage.requests.map((r) => r.tradePartner.id).includes(tp.id)),
      ...(showInternalEstimate ? [{ id: INTERNAL_ESTIMATE_ID, name: "Homebound Internal Estimate" }] : []),
    ],
    [bidPackage, showInternalEstimate, trades],
  );
  const noNewTrades = useMemo(() => matchingTrades.isEmpty, [matchingTrades.isEmpty]);
  // Sources to copy costs from: any active bidder (trade partner or internal estimate) with line items entered.
  const copySources = useMemo(
    () =>
      // Only V1 plan based doesn't support copying costs from other trades
      !bidPackage.isPlanBasedV2 && !bidPackage.isUnitBased
        ? []
        : bidPackage.activeBidders.flatMap((bidder) => {
            const rev = bidder.bidContract?.latestRevision;
            return rev && rev.lineItems.nonEmpty ? [{ id: rev.id, name: bidder.name }] : [];
          }),
    [bidPackage.activeBidders, bidPackage.isPlanBasedV2, bidPackage.isUnitBased],
  );
  const formState = useFormState({
    config: formConfig,
    init: {
      onlyOnce: true,
      input: {
        bidPackageId: bidPackage.id,
        // Autoselect the source when there is only one
        copySourceRevisionId: copySources.length === 1 ? copySources[0].id : undefined,
      },
    },
  });
  const showCopySourceSelect = useComputed(() => !!formState.copyCosts.value, [formState]);
  // Only PBBv2 offers the type choice
  const showInternalEstimateType = useComputed(
    () => bidPackage.isPlanBasedV2 && formState.tradePartnerId.value === INTERNAL_ESTIMATE_ID,
    [formState, bidPackage],
  );

  const saveAndExit = useCallback(async () => {
    const isInternalEstimate = formState.tradePartnerId.value === INTERNAL_ESTIMATE_ID;
    const copyCostsOn = !!formState.copyCosts.value;
    const copyContractRevisionId = copyCostsOn ? formState.copySourceRevisionId.value : undefined;
    if (isInternalEstimate) {
      // The estimate has no request, so all package flavors create its BC + draft BCR upfront
      await createBidContract({
        variables: {
          input: {
            isInternalEstimate: true,
            internalEstimateBidPackageId: bidPackage.id,
            internalEstimateType: formState.internalEstimateType.value,
            startDate: todayPT(),
            type: bidPackage.isPlanBasedV2
              ? BidContractType.UserCreatedGroupBased
              : bidPackage.isUnitBased
                ? BidContractType.UnitBased
                : BidContractType.GroupedByPlanAndOption,
            copyContractRevisionId,
          },
        },
      });
    } else if (bidPackage.isPlanBasedV2 || (bidPackage.isUnitBased && copyContractRevisionId)) {
      // Plan-based-v2 always creates the BC + draft BCR upfront so the "Enter Costs" page can
      // edit existing line items by BCR id instead of branching on "does the contract exist yet?".
      // V1 keeps the legacy "just create a request, defer the BC" flow.
      await createBidContract({
        variables: {
          input: {
            tradePartnerId: formState.tradePartnerId.value,
            bidPackageId: bidPackage.id,
            startDate: todayPT(),
            type: bidPackage.isUnitBased ? BidContractType.UnitBased : BidContractType.UserCreatedGroupBased,
            copyContractRevisionId,
          },
        },
      });
    } else {
      await saveBidPackageRequest({
        variables: { input: { bidPackageId: bidPackage.id, tradePartnerId: formState.tradePartnerId.value } },
      });
    }
    closeModal();
  }, [closeModal, formState, saveBidPackageRequest, createBidContract, bidPackage]);

  return (
    <div data-testid="addTradeModal">
      <ModalHeader>Add Trade</ModalHeader>
      <ModalBody>
        <div css={Css.df.fdc.gap3.$}>
          <div {...tid.tradeIndexHelperText}>
            If you don't see the trade you're looking for, visit the{" "}
            <Link {...tid.tradeIndexLink} to={createTradesTradePartnersUrl()} target="_blank" css={Css.smSb.blue600.$}>
              trade index
            </Link>{" "}
            and make sure they are assigned to your bid's cost code.
          </div>
          {noNewTrades ? (
            <span>No New Trades to Add</span>
          ) : (
            <FormLines>
              <BoundSelectField
                field={formState.tradePartnerId}
                label="Trade"
                options={matchingTrades}
                onSelect={(v) => {
                  if (v === INTERNAL_ESTIMATE_ID) {
                    formState.internalEstimateType.set(InternalEstimateType.Temporary);
                  }
                  formState.tradePartnerId.set(v);
                }}
              />
              {showInternalEstimateType && (
                <BoundRadioGroupField
                  label="Estimate Type"
                  layout="horizontal"
                  options={[
                    { label: "Temporary", value: InternalEstimateType.Temporary },
                    { label: "Lot Determined", value: InternalEstimateType.LotDetermined },
                  ]}
                  field={formState.internalEstimateType}
                />
              )}
              {copySources.nonEmpty && (
                <>
                  <BoundCheckboxField
                    fullWidth
                    field={formState.copyCosts}
                    label="Copy costs from another trade or internal estimate"
                  />
                  {showCopySourceSelect && (
                    <BoundSelectField
                      field={formState.copySourceRevisionId}
                      label="Copy costs from"
                      options={copySources}
                    />
                  )}
                </>
              )}
            </FormLines>
          )}
        </div>
      </ModalBody>
      <ModalFooter>
        <>
          <Button label="Cancel" onClick={closeModal} variant="quaternary" />
          <Button label="Save" onClick={saveAndExit} disabled={noNewTrades} />
        </>
      </ModalFooter>
    </div>
  );
}

type BidTradesFormValue = Omit<SaveBidPackageRequestInput, "primaryContactId" | "secondaryContactId"> & {
  internalEstimateType: Maybe<InternalEstimateType>;
  copyCosts: Maybe<boolean>;
  copySourceRevisionId: Maybe<string>;
};
const formConfig: ObjectConfig<BidTradesFormValue> = {
  bidPackageId: { type: "value" },
  tradePartnerId: { type: "value" },
  internalEstimateType: { type: "value" },
  copyCosts: { type: "value" },
  copySourceRevisionId: { type: "value" },
};
